import React from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import CircularProgress from "@material-ui/core/CircularProgress";

const useStyles = makeStyles({
  container: {
    backgroundColor: "#FFFFFF",
    width: "100%",
    boxSizing: "border-box",
    borderRadius: 0,
  },
  scrollContent: {
    maxHeight: "360px",
    overflowY: "auto",
    padding: "16px 20px",
    boxSizing: "border-box",
  },
  timelineItem: {
    display: "flex",
    position: "relative",
    paddingBottom: "14px",
  },
  dateCol: {
    width: "95px",
    flexShrink: 0,
    fontSize: "0.8125rem",
    color: "#555555",
    fontWeight: 500,
    paddingTop: "4px",
  },
  trackCol: {
    width: "36px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    position: "relative",
    flexShrink: 0,
  },
  verticalLine: {
    position: "absolute",
    top: "28px",
    bottom: 0,
    left: "50%",
    transform: "translateX(-50%)",
    width: "2px",
    backgroundColor: "#e0e0e0",
    zIndex: 0,
  },
  avatar: {
    width: "28px",
    height: "28px",
    borderRadius: "50%",
    backgroundColor: "#007BC0",
    color: "#FFFFFF",
    fontSize: "0.75rem",
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1,
    flexShrink: 0,
  },
  contentCol: {
    flex: 1,
    minWidth: 0,
    paddingLeft: "12px",
  },
  userName: {
    color: "#000000",
    fontWeight: 600,
    fontSize: "0.875rem",
  },
  statusRow: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    marginTop: "4px",
    flexWrap: "wrap",
  },
  badge: {
    borderRadius: "12px",
    padding: "2px 8px",
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#000000",
  },
  badgeCreated: {
    backgroundColor: "#e0e2e5",
  },
  badgeUpdated: {
    backgroundColor: "#FFDF95",
  },
  badgeApproved: {
    backgroundColor: "#B8EFC9",
  },
  badgeRejected: {
    backgroundColor: "#FCE8E6",
  },
  badgeLinked: {
    backgroundColor: "#D0E8FF",
    color: "#004975",
  },
  badgeUnlinked: {
    backgroundColor: "#E0E2E5",
    color: "#555555",
  },
  categoryLabel: {
    fontWeight: 700,
    fontSize: "0.8125rem",
    color: "#000000",
  },
  summaryText: {
    fontSize: "0.8125rem",
    color: "#555555",
    marginTop: "4px",
    lineHeight: 1.4,
  },
  detailsBox: {
    marginTop: "6px",
    padding: "8px 12px",
    backgroundColor: "#f9f9f9",
    border: "1px solid #e0e0e0",
    borderRadius: 0,
    fontSize: "0.75rem",
    color: "#333333",
  },
  commentBox: {
    marginTop: "6px",
    padding: "8px 12px",
    backgroundColor: "#FFF8F8",
    border: "1px solid #F8D7DA",
    borderRadius: 0,
    fontSize: "0.75rem",
    color: "#D50005",
  },
  emptyState: {
    padding: "32px 16px",
    textAlign: "center",
    color: "#555555",
    fontSize: "0.875rem",
  },
  loadingContainer: {
    padding: "40px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
});

function getInitials(name) {
  if (!name || typeof name !== "string") return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function GeneralBillingHistory({ items = [], loading = false }) {
  const { t } = useTranslation();
  const classes = useStyles();

  const getActionBadgeClass = (action) => {
    switch ((action || "").toUpperCase()) {
      case "CREATED":
        return classes.badgeCreated;
      case "UPDATED":
        return classes.badgeUpdated;
      case "APPROVED":
        return classes.badgeApproved;
      case "REJECTED":
        return classes.badgeRejected;
      case "LINKED":
        return classes.badgeLinked;
      case "UNLINKED":
        return classes.badgeUnlinked;
      default:
        return classes.badgeCreated;
    }
  };

  const getActionLabel = (action) => {
    switch ((action || "").toUpperCase()) {
      case "CREATED":
        return t("BillingPaymentOrders.ActionCreated");
      case "UPDATED":
        return t("BillingPaymentOrders.ActionUpdated");
      case "APPROVED":
        return t("BillingPaymentOrders.ActionApproved");
      case "REJECTED":
        return t("BillingPaymentOrders.ActionRejected");
      case "LINKED":
        return t("BillingPaymentOrders.ActionLinked");
      case "UNLINKED":
        return t("BillingPaymentOrders.ActionUnlinked");
      default:
        return action;
    }
  };

  const getCategoryLabel = (category) => {
    switch ((category || "").toUpperCase()) {
      case "PAYMENT_ORDER":
        return t("BillingPaymentOrders.CategoryPaymentOrder");
      case "TICKET":
        return t("BillingPaymentOrders.CategoryTicket");
      case "APPROVAL":
        return t("BillingPaymentOrders.CategoryApproval");
      default:
        return category;
    }
  };

  if (loading) {
    return (
      <div className={classes.container}>
        <div className={classes.loadingContainer}>
          <CircularProgress size={32} style={{ color: "#007BC0" }} />
        </div>
      </div>
    );
  }

  if (!items || items.length === 0) {
    return (
      <div className={classes.container}>
        <div className={classes.emptyState}>
          {t("BillingPaymentOrders.GeneralHistoryEmpty")}
        </div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <div className={classes.scrollContent}>
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          const badgeClass = `${classes.badge} ${getActionBadgeClass(item.action)}`;

          return (
            <div key={item.id || index} className={classes.timelineItem}>
              <div className={classes.dateCol}>
                <div>{item.date || "-"}</div>
                {item.time && <div>{item.time}</div>}
              </div>

              <div className={classes.trackCol}>
                {!isLast && <div className={classes.verticalLine} />}
                <div className={classes.avatar}>{getInitials(item.userName)}</div>
              </div>

              <div className={classes.contentCol}>
                <div className={classes.userName}>{item.userName || "-"}</div>
                <div className={classes.statusRow}>
                  <span className={badgeClass}>{getActionLabel(item.action)}</span>
                  {item.category && (
                    <span className={classes.categoryLabel}>
                      {getCategoryLabel(item.category)}
                      {item.opType ? ` - ${item.opType}` : ""}
                    </span>
                  )}
                </div>

                {item.changesSummary && (
                  <div className={classes.summaryText}>
                    {item.changesSummary}
                  </div>
                )}

                {item.totalValue != null && !item.changesSummary && (
                  <div className={classes.detailsBox}>
                    {item.quantity != null && <span>Qtd: {item.quantity} | </span>}
                    {item.unitValue != null && (
                      <span>Vlr. Unitário: R$ {Number(item.unitValue).toFixed(2)} | </span>
                    )}
                    <span>Total: R$ {Number(item.totalValue).toFixed(2)}</span>
                  </div>
                )}

                {item.comment && (
                  <div className={classes.commentBox}>
                    {item.comment}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
